// Central State Manager with Automatic Change Tracking
// Captures all state changes with full stack traces for error reporting
import { Logger } from './logger';
import { updateUrlState, encodeLocation, encodeFeatures } from './url-state';
import { mapStackTrace } from './stack-mapper';
import { resolveWebsiteURL } from './website-url';
const console = new Logger('StateManager');
const HISTORY_RETENTION_MS = 10000; // 10 seconds
const SYNC_DEBOUNCE_MS = 100; // Debounce URL updates
const ERROR_REPORT_ENDPOINT = '/api/errors/report';
function getWebsiteURL() {
    return resolveWebsiteURL();
}
function getSessionTokenFromCookie() {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
        const trimmedCookie = cookie.trim();
        const separatorIndex = trimmedCookie.indexOf('=');
        if (separatorIndex === -1) {
            continue;
        }
        const name = trimmedCookie.slice(0, separatorIndex);
        const value = trimmedCookie.slice(separatorIndex + 1);
        if (name === 'editor_session') {
            return value;
        }
    }
    return null;
}
function safeErrorReason(reason) {
    if (typeof reason === 'string') {
        return reason;
    }
    try {
        return JSON.stringify(reason);
    }
    catch (error) {
        return String(reason);
    }
}
function isSafariUserAgent(userAgent) {
    return (/Safari\//.test(userAgent) &&
        !/Chrome\//.test(userAgent) &&
        !/Chromium\//.test(userAgent) &&
        !/CriOS\//.test(userAgent) &&
        !/Edg\//.test(userAgent) &&
        !/OPR\//.test(userAgent) &&
        !/FxiOS\//.test(userAgent) &&
        !/Firefox\//.test(userAgent) &&
        !/Android/.test(userAgent));
}
function safeSessionStorageGet(key) {
    try {
        return window.sessionStorage.getItem(key);
    }
    catch (_error) {
        return null;
    }
}
function getErrorRuntimeContext() {
    const userAgent = navigator.userAgent || '';
    const hasServiceWorkerController = 'serviceWorker' in navigator && !!navigator.serviceWorker.controller;
    return {
        startupMs: Math.round(performance.now()),
        readyState: document.readyState,
        visibilityState: document.visibilityState,
        userAgent,
        browserFamily: isSafariUserAgent(userAgent) ? 'safari' : 'other',
        hasServiceWorkerController,
        serviceWorkerState: navigator.serviceWorker.controller?.state || null,
        coiReloadedBySelf: safeSessionStorageGet('coiReloadedBySelf'),
        hasSharedArrayBuffer: typeof SharedArrayBuffer !== 'undefined'
    };
}
async function sendErrorReportToServer(payload) {
    try {
        const websiteURL = getWebsiteURL();
        const sessionToken = window.authManager?.getSessionToken?.() ||
            getSessionTokenFromCookie();
        const headers = {
            'Content-Type': 'application/json'
        };
        if (sessionToken) {
            headers.Authorization = `Bearer ${sessionToken}`;
        }
        await fetch(`${websiteURL}${ERROR_REPORT_ENDPOINT}`, {
            method: 'POST',
            credentials: 'include',
            keepalive: true,
            headers,
            body: JSON.stringify(payload)
        });
    }
    catch (error) {
        console.warn('Failed to send error report to server:', error);
    }
}
/**
 * Build the full runtime-style error payload used for server reporting.
 */
export async function buildErrorReportPayload(error, source, actionContext) {
    const runtimeContext = getErrorRuntimeContext();
    const mappedReport = await stateManager.captureErrorMapped(error, actionContext);
    const snapshot = stateManager.getStateSnapshot();
    return {
        ...mappedReport,
        state: snapshot.state,
        history: snapshot.history,
        events: snapshot.events,
        source,
        reason: safeErrorReason(error),
        runtimeContext,
        url: window.location.href,
        userAgent: navigator.userAgent,
        appVersion: window.EDITOR_VERSION || null,
        buildHash: window.BUILD_HASH_FULL || null
    };
}
export class StateManager {
    constructor() {
        this._state = {};
        this._history = [];
        this._events = [];
        this._urlSyncEnabled = false;
        this._urlSyncDebounceTimer = null;
        // Root-level UI state
        this._state.focused_view = '';
        // Initialize with default editor state (flattened)
        this._state.editor_file = '';
        this._state.editor_text_buffer = 'Hamburgevons';
        this._state.editor_cursor_position = 0;
        this._state.editor_mode = 'text';
        this._state.editor_glyph_stack = '';
        this._state.editor_harfbuzz_glyph_names = '';
        this._state.editor_harfbuzz_gids = '';
        this._state.editor_harfbuzz_dx = '';
        this._state.editor_harfbuzz_dy = '';
        this._state.editor_harfbuzz_ax = '';
        this._state.editor_harfbuzz_ay = '';
        this._state.editor_harfbuzz_cl = '';
        this._state.editor_isInterpolating = false;
        this._state.editor_isAnimating = false;
        this._state.editor_opentype_features_in_subset = {};
        this._state.editor_opentype_features_not_in_subset = {};
        this._state.editor_variation_location = {};
        this._state.editor_active_canvas_plugins = [];
        console.log('StateManager initialized');
        // Return a Proxy to intercept all property access
        return new Proxy(this, {
            get: (target, prop) => {
                // Allow access to methods and private properties
                if (prop.startsWith('_') ||
                    typeof target[prop] === 'function') {
                    return target[prop];
                }
                // Return state values
                return target._state[prop];
            },
            set: (target, prop, value) => {
                // Don't track private properties or methods
                if (prop.startsWith('_')) {
                    target[prop] = value;
                    return true;
                }
                const oldValue = target._state[prop];
                // Only record if value actually changed
                if (oldValue === value) {
                    return true;
                }
                // Capture and clean stack trace
                const rawStack = new Error().stack || '';
                // Remove "Error" line and Proxy/StateManager internals
                const stack = rawStack
                    .split('\n')
                    .slice(1) // Remove "Error" line
                    .filter((line) => !line.includes('Proxy.set') &&
                    !line.includes('state-manager'))
                    .join('\n');
                // Record the change
                const change = {
                    timestamp: performance.now(),
                    key: prop,
                    oldValue: oldValue,
                    newValue: value,
                    stack: stack
                };
                target._history.push(change);
                target._state[prop] = value;
                // Map stack asynchronously so historical records point to original source
                mapStackTrace(stack)
                    .then((mappedStack) => {
                    if (mappedStack) {
                        change.stack = mappedStack;
                    }
                })
                    .catch((error) => {
                    console.warn('Failed to source-map history stack:', error);
                });
                // Prune old history and events
                target._pruneHistory();
                // Sync to URL if enabled (debounced)
                if (target._urlSyncEnabled) {
                    target._syncToUrl();
                }
                console.log(`State changed: ${prop}`);
                return true;
            }
        });
    }
    _pruneHistory() {
        const now = performance.now();
        const cutoff = now - HISTORY_RETENTION_MS;
        // Remove state changes older than retention window
        this._history = this._history.filter((change) => change.timestamp >= cutoff);
        // Remove events older than retention window
        this._events = this._events.filter((event) => event.timestamp >= cutoff);
    }
    _syncToUrl() {
        // Debounce URL updates to avoid excessive history entries
        if (this._urlSyncDebounceTimer !== null) {
            clearTimeout(this._urlSyncDebounceTimer);
        }
        this._urlSyncDebounceTimer = window.setTimeout(() => {
            this._urlSyncDebounceTimer = null;
            this._performUrlSync();
        }, SYNC_DEBOUNCE_MS);
    }
    _performUrlSync() {
        // Encode state to URL parameters (read from flat structure)
        const urlState = {};
        // File URI
        const fileUri = this._state.editor_file;
        if (fileUri) {
            urlState.file = fileUri;
        }
        // Text buffer (limit length)
        const textBuffer = this._state.editor_text_buffer;
        if (textBuffer && textBuffer.length < 200) {
            urlState.text = textBuffer;
        }
        // Cursor position
        urlState.cursor = this._state.editor_cursor_position;
        // Mode
        urlState.mode = this._state.editor_mode;
        // OpenType features (only active ones)
        const activeFeatures = Object.entries(this._state.editor_opentype_features_in_subset || {})
            .filter(([tag, enabled]) => enabled)
            .map(([tag, _]) => tag);
        if (activeFeatures.length > 0) {
            urlState.features = encodeFeatures(activeFeatures);
        }
        else {
            urlState.features = null;
        }
        // Variation location
        const varLocation = this._state.editor_variation_location;
        if (varLocation && Object.keys(varLocation).length > 0) {
            urlState.location = encodeLocation(varLocation);
        }
        else {
            urlState.location = null;
        }
        updateUrlState(urlState);
    }
    /**
     * Enable URL synchronization
     */
    enableUrlSync() {
        this._urlSyncEnabled = true;
        console.log('URL sync enabled');
    }
    /**
     * Disable URL synchronization
     */
    disableUrlSync() {
        this._urlSyncEnabled = false;
        if (this._urlSyncDebounceTimer !== null) {
            clearTimeout(this._urlSyncDebounceTimer);
            this._urlSyncDebounceTimer = null;
        }
        console.log('URL sync disabled');
    }
    /**
     * Check if URL sync is enabled
     */
    isUrlSyncEnabled() {
        return this._urlSyncEnabled;
    }
    /**
     * Flush URL synchronization immediately using current StateManager state.
     * Useful when downstream consumers need the URL to be up to date in the
     * same task, for example before reloading linked windows.
     */
    syncUrlNow() {
        if (!this._urlSyncEnabled) {
            return;
        }
        if (this._urlSyncDebounceTimer !== null) {
            clearTimeout(this._urlSyncDebounceTimer);
            this._urlSyncDebounceTimer = null;
        }
        this._performUrlSync();
    }
    /**
     * Get a snapshot of current state and history
     */
    getStateSnapshot() {
        return {
            state: JSON.parse(JSON.stringify(this._state)), // Deep copy
            history: [...this._history], // Shallow copy is fine
            events: JSON.parse(JSON.stringify(this._events))
        };
    }
    /**
     * Get a snapshot with source-mapped stack traces
     * Use this for error reporting to get readable stacks
     */
    async getStateSnapshotMapped() {
        const snapshot = this.getStateSnapshot();
        // Map all stack traces in parallel
        const mappedHistory = await Promise.all(snapshot.history.map(async (change) => ({
            ...change,
            stack: await mapStackTrace(change.stack)
        })));
        return {
            state: snapshot.state,
            history: mappedHistory,
            events: snapshot.events
        };
    }
    /**
     * Capture error with full state and history
     */
    captureError(error, actionContext) {
        const report = {
            error: {
                message: error?.message || String(error),
                stack: error?.stack || ''
            },
            timestamp: performance.now(),
            state: JSON.parse(JSON.stringify(this._state)),
            history: [...this._history],
            events: JSON.parse(JSON.stringify(this._events))
        };
        // Log the error report (will be source-mapped by browser devtools)
        console.error('[StateManager] Error captured:');
        console.error(JSON.stringify(report, null, 2));
        // Also log source-mapped version asynchronously
        this.captureErrorMapped(error, actionContext).then((mappedReport) => {
            console.error('[StateManager] Error captured (source-mapped):');
            console.error(JSON.stringify(mappedReport, null, 2));
        });
        return report;
    }
    /**
     * Capture error with source-mapped stack traces
     * Use this for sending error reports with readable stacks
     */
    async captureErrorMapped(error, actionContext) {
        const snapshot = await this.getStateSnapshotMapped();
        const report = {
            error: {
                message: error?.message || String(error),
                stack: await mapStackTrace(error?.stack || '')
            },
            timestamp: performance.now(),
            state: snapshot.state,
            history: snapshot.history,
            events: snapshot.events
        };
        return report;
    }
    /**
     * Clear history (for testing/debugging)
     */
    clearHistory() {
        this._history = [];
        this._events = [];
        console.log('History cleared');
    }
    /**
     * Get history for a specific key or time range
     */
    getHistory(key, since) {
        let filtered = this._history;
        if (key) {
            filtered = filtered.filter((change) => change.key === key);
        }
        if (since) {
            filtered = filtered.filter((change) => change.timestamp >= since);
        }
        return filtered;
    }
    /**
     * Record a high-level event for debugging timelines
     */
    recordEvent(type, source, payload) {
        const rawStack = new Error().stack || '';
        const stack = rawStack
            .split('\n')
            .slice(1)
            .filter((line) => !line.includes('recordEvent') &&
            !line.includes('state-manager'))
            .join('\n');
        const event = {
            timestamp: performance.now(),
            type,
            source,
            payload,
            stack
        };
        this._events.push(event);
        mapStackTrace(stack)
            .then((mappedStack) => {
            if (mappedStack) {
                event.stack = mappedStack;
            }
        })
            .catch((error) => {
            console.warn('Failed to source-map event stack:', error);
        });
        this._pruneHistory();
    }
    /**
     * Get event history for a specific event type or time range
     */
    getEventHistory(type, since) {
        let filtered = this._events;
        if (type) {
            filtered = filtered.filter((event) => event.type === type);
        }
        if (since) {
            filtered = filtered.filter((event) => event.timestamp >= since);
        }
        return filtered;
    }
    /**
     * Export state and history as pretty JSON
     */
    exportJSON() {
        return JSON.stringify(this.getStateSnapshot(), null, 2);
    }
    /**
     * Export state and history with source-mapped stacks
     * Use this for error reporting
     */
    async exportJSONMapped() {
        const snapshot = await this.getStateSnapshotMapped();
        return JSON.stringify(snapshot, null, 2);
    }
    /**
     * Get direct access to state (for reading, not mutation)
     */
    getState() {
        return this._state;
    }
    /**
     * Get direct access to history array
     */
    get history() {
        return this._history;
    }
    /**
     * Get direct access to event history array
     */
    get events() {
        return this._events;
    }
}
// Export singleton instance creation helper
export function createStateManager() {
    return new StateManager();
}
// Create singleton instance
const stateManager = new StateManager();
// Assign to window
window.stateManager = stateManager;
window.triggerRuntimeErrorForTesting = (message) => {
    const errorMessage = message ||
        `Counterpunch runtime error test (${new Date().toISOString()})`;
    window.setTimeout(() => {
        throw new Error(errorMessage);
    }, 0);
};
window.triggerUnhandledRejectionForTesting = (message) => {
    const errorMessage = message ||
        `Counterpunch unhandled rejection test (${new Date().toISOString()})`;
    Promise.reject(new Error(errorMessage));
};
// Install global error handlers
window.addEventListener('error', async (event) => {
    const reason = event.error || event.message || 'Unknown window error';
    stateManager.captureError(reason, 'window.error');
    const payload = await buildErrorReportPayload(reason, 'editor.window.error', 'window.error');
    void sendErrorReportToServer(payload);
});
window.addEventListener('unhandledrejection', async (event) => {
    const reason = event.reason || 'Unhandled promise rejection';
    stateManager.captureError(reason, 'window.unhandledrejection');
    const payload = await buildErrorReportPayload(reason, 'editor.window.unhandledrejection', 'window.unhandledrejection');
    void sendErrorReportToServer(payload);
});
export default stateManager;
